/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { BackgroundContainer, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { css, cx } from "@panda/css";
import { TabbedContentImageItemFragment, TabbedContentImageSectionFragment } from "@cosmos/hygraph-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TabbedButtonList } from "@components/elements/molecules/tabbed-button-list";
import { TabbedContentImageContent } from "./TabbedContentImage.Content";
import { VStack } from "@panda/jsx";
import { SectionHeading } from "@components/elements/molecules/section-heading";

const cardStyles = css({
	w: "full",
	backdropFilter: "blur(50px)",
	borderRadius: "eds.borderRadius.32",
	background: "eds.palette.transparent.light.005",
	border: "1px solid",
	borderColor: "eds.palette.transparent.light.005",
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.40",
	padding: "eds.dimension.64",
	mdDown: {
		padding: "eds.dimension.48",
	},
	smDown: {
		paddingInline: "eds.dimension.24",
	},
});

const analyticsEventValue = {
	component: "TabbedContentImage",
	section: "section",
};

export const TabbedContentImageSection = (props: TabbedContentImageSectionFragment): React.JSX.Element | null => {
	const config = props.config || {};
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);
	const active = tabs[0] || {};
	const [activeTab, setActiveTab] = useState<TabbedContentImageItemFragment>(active);

	useEffect(() => {
		if (tabs.length > 0 && !activeTab?.id) setActiveTab(tabs[0]);
	}, [tabs, activeTab]);

	const tabsRef = useRef(tabs);
	useEffect(() => {
		tabsRef.current = tabs;
	}, [tabs]);

	const handleButtonClick = useCallback((index: number) => {
		const tab = Number.isInteger(index) ? tabsRef.current.at(index) : undefined;
		if (tab) setActiveTab(tab);
	}, []);

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = useMemo(
		() => Boolean(tabs?.length && (activeTab?.asset || activeTab?.title?.text || activeTab?.desc)),
		[tabs?.length, activeTab],
	);
	if (!validHeading && !validContent) return null;

	const enableTabs = tabs.length > 1;

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer>
					<VStack gap="eds.dimension.56" alignItems="flex-start" className="tabbed-content-image">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<div className={cx("card", cardStyles)}>
								{tabs?.length > 1 ? (
									<TabbedButtonList
										onTabChange={handleButtonClick}
										tabs={tabs}
										analyticsEventValue={analyticsEventValue}
										className={css({
											gap: "eds.dimension.12",
											"& button": { paddingInline: "eds.dimension.16" },
										})}
									/>
								) : null}
								{activeTab?.asset || activeTab?.title?.text || activeTab?.desc ? (
									<TabbedContentImageContent singleTab={!enableTabs} {...activeTab} />
								) : null}
							</div>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
